import {
  all,
  put,
  takeLatest,
  call,
  select,
  takeEvery,
  delay,
} from 'redux-saga/effects'
import { push } from 'connected-react-router'

import omit from 'utils/omit'
import ObjectSet from 'utils/ObjectSet'
import deleteFromObjByKeys from 'utils/deleteFromObjByKeys'

import * as actionTypes from './action-types'
import * as actionTypesModal from '../modal/action-types'
import * as actionTypesError from '../errors/action-types'
import { fetchVendors, addVendor, removeVendors } from './api'
import { fetchInvitations } from '../invitations/actions'
import { vendorsToObjectsItemsInfo, formatSearchOptions } from './business'
import {
  getSelectedIds,
  getVendorsIds,
  getSearchFilters,
  getCurrentPage,
  getVendorsList,
} from './selectors'

function* fetchVendorsSaga({
  payload: { page, filters, removeLastPage = false },
}) {
  try {
    const { keyword } = yield select(getSearchFilters)
    let pageToFetch = page

    if (!pageToFetch) {
      pageToFetch = yield select(getCurrentPage)
    }

    const currentPageResponse = yield call(fetchVendors, {
      page: pageToFetch,
      keyword,
      filters,
    })
    const nextPageResponse = yield call(fetchVendors, {
      page: pageToFetch + 1,
      keyword,
      filters,
    })

    const {
      _embedded: currentVendorsList,
      _links,
      ...pagination
    } = currentPageResponse
    const currentVendors = currentVendorsList ? currentVendorsList.networks : []

    const { _embedded: nextVendorsList } = nextPageResponse
    const nextVendors = nextVendorsList ? nextVendorsList.networks : []

    const totalVendors = currentVendors
      ? vendorsToObjectsItemsInfo([...currentVendors, ...(nextVendors || [])])
      : {}

    yield put({
      type: actionTypes.SET_VENDORS,
      payload: totalVendors,
    })

    yield put({
      type: actionTypes.SET_PAGINATION,
      payload: {
        ...pagination,
        pages: removeLastPage ? pagination.pages - 1 : pagination.pages,
      },
    })
  } catch (e) {
    yield put({ type: actionTypes.SET_FETCH_VENDORS_ERROR, payload: e.message })
    yield put({ type: actionTypesError.ERROR, payload: { error: e } })
  }
}

function* selectVendorSaga({ payload: { id } }) {
  const selectedIds = yield select(getSelectedIds)
  const updatedSelected = ObjectSet.toggle(selectedIds, id)
  yield put({
    type: actionTypes.SET_SELECTED,
    payload: updatedSelected,
  })
}

function* selectAllSaga() {
  const vendorsIds = yield select(getVendorsIds)
  const currentSelected = yield select(getSelectedIds)
  const newSelected = {}
  vendorsIds.slice(0, 10).forEach(vendorId => (newSelected[vendorId] = true))
  yield put({
    type: actionTypes.SET_SELECTED,
    payload: { ...currentSelected, ...newSelected },
  })
}

function* resetSelectedData() {
  const vendorsIds = yield select(getVendorsIds)
  const currentSelected = yield select(getSelectedIds)
  const newSelected = omit(vendorsIds, currentSelected)
  yield put({
    type: actionTypes.SET_SELECTED,
    payload: { ...newSelected },
  })
}

function* addVendorSaga({ payload: data }) {
  try {
    const response = yield call(addVendor, data)
    yield put({ type: actionTypes.ADD_VENDOR_SUCCESS })
    yield put({ type: actionTypes.ADD_VENDOR_CONFIRM, payload: true })
    yield delay(300)
    yield put({ type: actionTypesModal.CLOSE_MODAL })
    yield put({ type: actionTypes.ADD_VENDOR_CLEAR })
    yield put(push('/app/vendors/invitations'))
    yield put(fetchInvitations({ keyword: response.identification_code }))
  } catch (e) {
    const errors = yield call([e, 'json'])
    if (e.status === 404) {
      yield put({
        type: actionTypes.ADD_VENDOR_NOT_REGISTERED,
        payload: { notRegistered: true },
      })
    } else if (e.status === 409) {
      yield put({ type: actionTypes.ADD_VENDOR_SUCCESS })
      yield put({ type: actionTypes.ADD_VENDOR_CONFIRM, payload: true })
      yield delay(300)
      yield put({ type: actionTypesModal.CLOSE_MODAL })
      yield put({ type: actionTypes.ADD_VENDOR_CLEAR })
      yield put(push('/app/vendors/invitations'))
    } else if (e.status === 400 || e.status === 500) {
      if (errors.invalid_params) {
        const invalidParamsError = errors.invalid_params
          .map(error => ({ [error.name]: error.reason }))
          .reduce((prev, curr) => ({ ...prev, ...curr }), {})
        yield put({
          type: actionTypes.ADD_VENDOR_NOT_REGISTERED,
          payload: invalidParamsError,
        })
      }
      if (errors.detail) {
        yield put({
          type: actionTypes.ADD_VENDOR_NOT_REGISTERED,
          payload: { generic: errors.detail },
        })
      }
      return false
    } else {
      yield put({ type: actionTypesError.ERROR, payload: { error: e } })
    }
  }
}

function* confirmAddVendorFake() {
  yield put({ type: actionTypes.ADD_VENDOR_CONFIRM, payload: true })
  yield delay(300)
  yield put(push('/app/vendors/invitations'))
  yield put({ type: actionTypesModal.CLOSE_MODAL })
  yield put({ type: actionTypes.ADD_VENDOR_CLEAR })
}

function* getSearchOptionsSaga({ payload: { keyword } }) {
  try {
    const response = yield call(fetchVendors, { keyword, limit: 50 })
    const vendors = formatSearchOptions((response._embedded || {}).networks)

    yield put({ type: actionTypes.SET_SEARCH_OPTIONS, payload: vendors })
  } catch (e) {
    yield put({ type: actionTypesError.ERROR, payload: { error: e } })
  }
}

function* removeVendorsSaga({ payload: { ids, filters } }) {
  try {
    yield call(removeVendors, ids)
    yield put({ type: actionTypes.REMOVE_SUCCESS })
    yield put({
      type: actionTypes.FETCH_VENDORS,
      payload: { page: null, filters },
    })
    yield put({ type: actionTypes.RESET_SELECTED })
  } catch (e) {
    yield put({ type: actionTypes.REMOVE_ERROR })
    yield put({ type: actionTypesError.ERROR, payload: { error: e } })
  }
}

function* fakeRemoveSaga({ payload: { ids, filters } }) {
  const vendors = yield select(getVendorsList)
  const filteredVendors = yield call(deleteFromObjByKeys, vendors, ids)
  const currentPage = yield select(getCurrentPage)

  if (!Object.keys(filteredVendors).length && currentPage !== 1) {
    yield put({
      type: actionTypes.FETCH_VENDORS,
      payload: { page: currentPage - 1, filters, removeLastPage: true },
    })
    yield put({ type: actionTypes.RESET_SELECTED })
  } else {
    yield put({ type: actionTypes.SET_VENDORS, payload: filteredVendors })
  }
}

export default function* rootSaga() {
  yield all([
    takeLatest(actionTypes.FETCH_VENDORS, fetchVendorsSaga),
    takeLatest(actionTypes.SELECT_VENDOR, selectVendorSaga),
    takeLatest(actionTypes.SELECT_ALL, selectAllSaga),
    takeLatest(actionTypes.RESET_SELECTED_ON_PAGE, resetSelectedData),
    takeEvery(actionTypes.ADD_VENDOR, addVendorSaga),
    takeLatest(actionTypes.GET_SEARCH_OPTIONS, getSearchOptionsSaga),
    takeLatest(actionTypes.CONFIRM_ADD_VENDOR_FAKE, confirmAddVendorFake),
    takeEvery(actionTypes.REMOVE, removeVendorsSaga),
    takeEvery(actionTypes.REMOVE_FAKE, fakeRemoveSaga),
  ])
}
